import {useEffect, useRef, useState, type ReactNode} from 'react';
import {QRCodeSVG} from 'qrcode.react';
import {useSetAtom} from 'jotai';
import {toast} from 'sonner';
import {Copy, ExternalLink, Check, Loader2, AlertTriangle} from 'lucide-react';
import {
  notificationsConnectionAtom,
  type NotificationsConnection,
} from '@lib/atoms';
import {useChannels, useInitChannel} from '@api/notifications';
import type {ChannelName} from '@api/notifications';
import {Button} from '@ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@ui/shadcn/dialog';
import {
  NOTIFICATION_CHANNELS,
  channelMeta,
  type NotificationChannelMeta,
} from '@ui/local/notification-channels';

/** Шаг флоу: выбор канала либо подключение конкретного канала. */
type Step = 'choose' | ChannelName;

interface ConnectFlowProps {
  open: boolean;
  onClose: () => void;
  /** Текущее состояние подключений — используется чтобы пометить какой канал ставим. */
  connection: NotificationsConnection;
  /** Если задан — пропустить выбор канала и сразу перейти к подключению. */
  startChannel?: ChannelName;
}

// ─── Shared modal shell ───────────────────────────────────────────────────

const ModalShell = ({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) => {
  const returnFocusRef = useRef<HTMLElement | null>(null);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        overlayClassName='z-[calc(var(--z-modal-task)+2)]!'
        className='z-[calc(var(--z-modal-task)+3)]! max-h-[calc(100dvh-2rem)] overflow-y-auto gap-0 rounded-xl bg-[var(--color-surface)] p-0 sm:max-w-[480px]'
        aria-describedby={undefined}
        onOpenAutoFocus={() => {
          returnFocusRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
        }}
        onCloseAutoFocus={(event) => {
          if (returnFocusRef.current?.isConnected) {
            event.preventDefault();
            returnFocusRef.current.focus();
          }
        }}
      >
        <DialogHeader className='px-5 py-4 pr-12 border-b border-[var(--color-border)] text-left'>
          <DialogTitle className='text-sm'>{title}</DialogTitle>
        </DialogHeader>
        <div className='p-5'>{children}</div>
        {footer && (
          <DialogFooter className='px-5 py-3 border-t border-[var(--color-border)] bg-[var(--color-muted)] sm:justify-start'>
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Step 1: choose channel ────────────────────────────────────────────────

const ChannelCard = ({
  onClick,
  icon,
  iconBg,
  iconColor = '#ffffff',
  title,
  description,
}: {
  onClick: () => void;
  icon: ReactNode;
  iconBg: string;
  /** Цвет иконки (по умолчанию белый — для цветных подложек). */
  iconColor?: string;
  title: string;
  description: string;
}) => (
  <Button
    type='button'
    variant='outline'
    onClick={onClick}
    className='flex-1 h-auto flex-col gap-3 p-5 rounded-xl font-normal whitespace-normal hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-soft)] hover:shadow-md'
  >
    <span
      className='w-12 h-12 rounded-xl flex items-center justify-center border border-[var(--color-border)]'
      style={{backgroundColor: iconBg, color: iconColor}}
    >
      {icon}
    </span>
    <span className='text-sm font-semibold text-[var(--color-foreground)]'>
      {title}
    </span>
    <span className='text-[11px] text-[var(--color-muted-foreground)] text-center leading-snug'>
      {description}
    </span>
  </Button>
);

const ChooseChannelStep = ({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (s: Step) => void;
}) => (
  <ModalShell title='Выбор канала уведомлений' onClose={onClose}>
    <div className='flex items-stretch gap-3'>
      {NOTIFICATION_CHANNELS.map((channel) => (
        <ChannelCard
          key={channel.name}
          onClick={() => onPick(channel.name)}
          icon={channel.icon(22)}
          iconBg={channel.brandColor}
          title={channel.cardTitle}
          description={channel.cardDescription}
        />
      ))}
    </div>
  </ModalShell>
);

// ─── Step 2: подключение канала-бота (Telegram / MAX) ──────────────────────

/**
 * Общий шаг подключения бота: init → QR + код → «Я ввёл код» → poll /channels
 * пока не увидим `connected`, тогда onSuccess. Каналы отличаются только
 * оформлением и ссылкой на бота (`channelMeta`).
 */
const BotConnectStep = ({
  channel,
  onClose,
  onSuccess,
}: {
  channel: NotificationChannelMeta;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const initChannel = useInitChannel();
  const {data: init, isError: initFailed} = initChannel;
  const code = init?.code ?? '';
  // Ссылка из ответа бэка приоритетнее — постоянный URL лишь фолбэк.
  const botLink = init?.botLink || channel.botUrl;
  // Poll /channels с лимитом 3 попытки. После — «код не подтверждён».
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 3;
  const verifying = pollCount > 0 && pollCount <= maxPolls;

  const {data: channels} = useChannels(verifying);
  const connection = channels?.find((c) => c.channel === channel.name);

  useEffect(() => {
    initChannel.mutate(channel.name);
  }, [channel.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Инкремент pollCount на каждый ответ (через 3с от refetchInterval).
  useEffect(() => {
    if (!verifying) return;
    const timer = setTimeout(() => {
      setPollCount((c) => c + 1);
    }, 3000);
    return () => clearTimeout(timer);
  }, [verifying, pollCount, channels]);

  // Результат проверки.
  useEffect(() => {
    if (!verifying) return;
    if (connection?.connectionState === 'connected') {
      setPollCount(0);
      onSuccess();
    } else if (pollCount >= maxPolls) {
      setPollCount(0);
      toast.error(
        'Подключение не подтверждено. Проверьте код и попробуйте снова.',
        {duration: 3000},
      );
    }
  }, [verifying, connection, pollCount, onSuccess]);

  // Сброс при закрытии/отмене — не считаем подключённым.
  useEffect(() => {
    return () => setPollCount(0);
  }, []);

  const handleConfirm = () => {
    setPollCount(1);
    toast.info('Проверяем подключение…', {duration: 2000});
  };

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Код скопирован', {duration: 2000});
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  return (
    <ModalShell
      title={channel.connectTitle}
      onClose={onClose}
      footer={
        <div className='flex items-center gap-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={onClose}
            className='text-xs text-[var(--color-secondary)]'
          >
            Отмена
          </Button>
          <Button
            type='button'
            size='sm'
            onClick={handleConfirm}
            disabled={verifying || !code}
            className='text-xs'
          >
            {verifying ? (
              <>
                <Loader2 size={12} className='animate-spin' /> Проверка…
              </>
            ) : (
              <>
                <Check size={12} /> Я ввёл код в боте
              </>
            )}
          </Button>
        </div>
      }
    >
      {initFailed ? (
        // Канал может быть ещё не поднят на бэкенде — показываем причину,
        // а не бесконечную «Загрузку…».
        <div className='flex flex-col items-center gap-2 py-6 text-center'>
          <AlertTriangle size={22} className='text-[var(--score-warn)]' />
          <p className='text-sm font-medium text-[var(--color-foreground)]'>
            Не удалось получить код подключения
          </p>
          <p className='text-xs text-[var(--color-muted-foreground)] max-w-[320px] leading-relaxed'>
            Канал {channel.label} сейчас недоступен. Попробуйте позже или
            обратитесь в поддержку.
          </p>
        </div>
      ) : (
        <div className='flex flex-col items-center gap-4'>
          <div className='p-3 rounded-xl border border-[var(--color-border)] bg-white'>
            {code ? (
              <QRCodeSVG value={botLink} size={160} />
            ) : (
              <div className='w-[160px] h-[160px] flex items-center justify-center text-[var(--color-muted-foreground)] text-xs'>
                Загрузка…
              </div>
            )}
          </div>

          <p className='text-xs text-[var(--color-muted-foreground)] text-center leading-relaxed max-w-[380px]'>
            Отсканируйте QR-код с камеры мобильного телефона или перейдите по
            ссылке. В открывшемся боте нажмите «Старт» и введите код.
          </p>

          <div className='w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-[var(--color-muted)] border border-[var(--color-border)]'>
            <span className='font-mono text-sm font-semibold text-[var(--color-foreground)] tracking-wider'>
              {code || '…'}
            </span>
            <Button
              type='button'
              variant='link'
              size='xs'
              onClick={copyCode}
              className='gap-1 px-0 text-xs text-[var(--color-primary)] no-underline hover:underline'
            >
              <Copy size={12} /> Копировать
            </Button>
          </div>

          <a
            href={botLink}
            target='_blank'
            rel='noopener noreferrer'
            className='w-full inline-flex items-center justify-center gap-2 text-xs px-3 py-2.5 rounded-lg bg-[var(--color-primary)] text-white font-medium hover:bg-[var(--color-primary-hover)]'
          >
            <ExternalLink size={13} /> {channel.openLabel}
          </a>
        </div>
      )}
    </ModalShell>
  );
};

// ─── Orchestrator ──────────────────────────────────────────────────────────

/** Многошаговый flow подключения уведомлений: выбор канала → форма → success. */
export const ConnectFlow = ({
  open,
  onClose,
  connection,
  startChannel,
}: ConnectFlowProps) => {
  const [step, setStep] = useState<Step>(startChannel ?? 'choose');
  const setConnection = useSetAtom(notificationsConnectionAtom);

  // Сброс шага при каждом открытии
  useEffect(() => {
    if (open) setStep(startChannel ?? 'choose');
  }, [open, startChannel]);

  if (!open) return null;

  const finishChannel = (channel: NotificationChannelMeta) => {
    setConnection({...connection, [channel.name]: true});
    toast.success(`${channel.cardTitle} подключён`, {duration: 3000});
    onClose();
  };

  return (
    <>
      {step === 'choose' ? (
        <ChooseChannelStep onClose={onClose} onPick={setStep} />
      ) : (
        <BotConnectStep
          key={step}
          channel={channelMeta(step)}
          onClose={onClose}
          onSuccess={() => finishChannel(channelMeta(step))}
        />
      )}
    </>
  );
};
